import { ReactElement } from 'react'
import { Allotment } from 'allotment'
import create from 'zustand'
import { Box } from '@mui/material'
import AIChatDrawerSlot from '@editor/components/AIChatDrawerSlot'
import { NavbarPortalLeft } from '@ui/navigation'
import { LayoutDrawer } from '@ui/structure'
import { useModelStore } from '@editorStores'
import { useEnterEditorPage } from '@editorHooks'
import ProjectAIInstructions from 'src/components/manager/aiInstructions/ProjectAIInstructions'
import ProjectDefaultMemberCrossSections from 'src/components/manager/defaultCrossSections/ProjectDefaultMemberCrossSections'
import ProjectSettingsSection from 'src/components/manager/projectConfiguration/ProjectSettingsSection'
import ManagerTabs from 'src/components/pages/Manager/components/ManagerTabs'
import useHasAiPermission from 'src/hooks/useHasAiPermission'
import useUIStore from 'src/state/stores/ui'
import AssemblyManagerForm from './components/AssemblyManagerForm'
import ConnectionManagerForm from './components/ConnectionManagerForm'
import ShearConnectionManagerForm from './components/ShearConnectionManagerForm'

// 'plansDocumentation' only exists in the global system manager; the tab is
// not rendered here, but the type must match the shared ManagerTabs props.
type ActiveTab =
  | 'assemblies'
  | 'connections'
  | 'shearConnections'
  | 'defaultCrossSections'
  | 'components'
  | 'projectFields'
  | 'plansDocumentation'
  | 'aiInstructions'

/** Active system manager tab (formerly the `tab` query param; session-only by design). */
const useProjectSystemManagerTabStore = create<{
  activeTab: ActiveTab
  setActiveTab: (activeTab: ActiveTab) => void
}>(set => ({
  activeTab: 'assemblies',
  setActiveTab: activeTab => set({ activeTab }),
}))

const ProjectSystemManager = (): ReactElement => {
  useEnterEditorPage('project-system-manager')

  const project = useModelStore(state => state.project)
  const activeTab = useProjectSystemManagerTabStore(state => state.activeTab)
  const setActiveTab = useProjectSystemManagerTabStore(state => state.setActiveTab)
  const { hasAiPermission } = useHasAiPermission()
  const aiChatOpen = useUIStore(state => state.aiChatOpen)

  return (
    <>
      <LayoutDrawer overlayingDrawer>
        <Box sx={{ flex: 1, minWidth: 0, height: '100%' }}>
          <Allotment id="project-system-manager-allotment">
            <Allotment.Pane minSize={500}>
              <Box
                sx={({ spacing }) => ({
                  height: '100%',
                  padding: spacing(3),
                  paddingLeft: spacing(9),
                  overflowY: 'auto',
                })}
              >
                <ManagerTabs
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  assemblyManager={
                    <AssemblyManagerForm earthMassForce={project.earth_mass_force} />
                  }
                  connectionManager={<ConnectionManagerForm />}
                  shearConnectionManager={<ShearConnectionManagerForm />}
                  defaultCrossSectionsManager={<ProjectDefaultMemberCrossSections />}
                  projectFieldsManager={<ProjectSettingsSection />}
                  aiInstructionsManager={
                    hasAiPermission ? (
                      <ProjectAIInstructions projectGuid={project.guid} />
                    ) : undefined
                  }
                />
              </Box>
            </Allotment.Pane>
            {/* The right drawer: so far only the AI chat lives here. */}
            <Allotment.Pane minSize={320} preferredSize={455} visible={aiChatOpen}>
              <Box
                sx={({ palette }) => ({
                  height: '100%',
                  backgroundColor: palette.common.white,
                  borderLeft: `1px solid ${palette.divider}`,
                })}
              >
                <AIChatDrawerSlot />
              </Box>
            </Allotment.Pane>
          </Allotment>
        </Box>
      </LayoutDrawer>

      <NavbarPortalLeft>
        <Box></Box>
      </NavbarPortalLeft>
    </>
  )
}

export default ProjectSystemManager
